import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth.jsx';
import { useNavigate } from 'react-router-dom';
import { Package, Calendar, Truck, Clock, CheckCircle, XCircle, AlertCircle, Eye, Download, Filter, Search, Smartphone } from 'lucide-react';
import jsPDF from 'jspdf';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%);
  padding: 6rem 1.5rem 3rem;
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBar = styled.div`
  flex: 1;
  min-width: 250px;
  position: relative;
  
  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: white;
    font-size: 0.9rem;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      background: rgba(255, 255, 255, 0.15);
    }
  }
  
  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.5);
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 0.9rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
  
  option {
    background: #1a1a2e;
    color: white;
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
`;

const OrderCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  .order-number {
    font-size: 1.1rem;
    font-weight: 600;
    color: #3b82f6;
  }
  
  .order-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    
    &.pending {
      background: rgba(251, 191, 36, 0.2);
      color: #fbbf24;
    }
    
    &.processing {
      background: rgba(59, 130, 246, 0.2);
      color: #3b82f6;
    }
    
    &.shipped {
      background: rgba(139, 92, 246, 0.2);
      color: #8b5cf6;
    }
    
    &.delivered {
      background: rgba(34, 197, 94, 0.2);
      color: #22c55e;
    }
    
    &.cancelled {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
    }
  }
`;

const OrderDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const OrderItems = styled.div`
  margin-bottom: 1rem;
  
  .item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    
    &:last-child {
      border-bottom: none;
    }
    
    .item-icon {
      width: 50px;
      height: 50px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #3b82f6, #9333ea);
      color: white;
      font-size: 1.5rem;
      flex-shrink: 0;
    }
    
    .item-details {
      flex: 1;
      
      .item-name {
        font-weight: 600;
        margin-bottom: 0.25rem;
      }
      
      .item-config {
        font-size: 0.8rem;
        color: rgba(255, 255, 255, 0.6);
      }
    }
    
    .item-quantity {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.9rem;
    }
  }
`;

const OrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .order-total {
    font-size: 1.2rem;
    font-weight: 700;
    color: #22c55e;
  }
  
  .order-actions {
    display: flex;
    gap: 0.5rem;
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  .icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 2rem;
    color: rgba(255, 255, 255, 0.3);
  }
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: rgba(255, 255, 255, 0.8);
  }
  
  p {
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 2rem;
  }
`;

const ShopButton = styled.button`
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  .icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 1rem;
    color: #3b82f6;
    animation: spin 1s linear infinite;
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  }
`;

const OrdersPageFixed = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Simuler des données de commandes
  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      
      // Simuler un appel API
      setTimeout(() => {
        const mockOrders = [
          {
            id: 'ORD-2024-001',
            date: '2024-12-10',
            status: 'delivered',
            total: 918000,
            items: [
              {
                id: 1,
                name: 'iPhone 15 Pro Max',
                config: '256GB - Bleu Titane',
                quantity: 1,
                price: 918000
              }
            ],
            paymentMethod: 'Wave',
            shippingAddress: 'Abidjan, Cocody'
          },
          {
            id: 'ORD-2024-002',
            date: '2024-12-08',
            status: 'shipped',
            total: 587000,
            items: [
              {
                id: 2,
                name: 'iPhone 15',
                config: '128GB - Noir Titane',
                quantity: 1,
                price: 587000
              }
            ],
            paymentMethod: 'Cash on Delivery',
            shippingAddress: 'Abidjan, Plateau'
          },
          {
            id: 'ORD-2024-003',
            date: '2024-12-05',
            status: 'processing',
            total: 1456000,
            items: [
              {
                id: 3,
                name: 'iPhone 15 Pro',
                config: '512GB - Blanc Titane',
                quantity: 2,
                price: 728000
              }
            ],
            paymentMethod: 'Wave',
            shippingAddress: 'Abidjan, Yopougon'
          }
        ];
        
        setOrders(mockOrders);
        setLoading(false);
      }, 1500);
    };
    
    if (isAuthenticated) {
      loadOrders();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} />;
      case 'processing': return <AlertCircle size={16} />;
      case 'shipped': return <Truck size={16} />;
      case 'delivered': return <CheckCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <Package size={16} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'processing': return 'En traitement';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewDetails = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleDownloadInvoice = (order) => {
    // Créer un nouveau document PDF
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const leftColumn = margin;
    const rightColumn = pageWidth - margin - 80;
    let yPosition = margin;
    
    // En-tête avec logo et titre
    pdf.setFillColor(59, 130, 246);
    pdf.rect(0, 0, pageWidth, 60, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('INZA STORE', leftColumn, 35);
    
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'normal');
    pdf.text('FACTURE', pageWidth - margin, 35, { align: 'right' });
    
    // Informations de la commande
    pdf.setTextColor(0, 0, 0);
    yPosition = 80;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Facture #: ${order.id}`, leftColumn, yPosition);
    yPosition += 6;
    pdf.text(`Date: ${formatDate(order.date)}`, leftColumn, yPosition);
    yPosition += 6;
    pdf.text(`Statut: ${getStatusText(order.status)}`, leftColumn, yPosition);
    
    // Informations client (colonne gauche)
    yPosition += 15;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CLIENT', leftColumn, yPosition);
    yPosition += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Utilisateur INZA', leftColumn, yPosition);
    yPosition += 5;
    pdf.text('user@inza.com', leftColumn, yPosition);
    yPosition += 5;
    pdf.text('+225 07 00 00 00 00', leftColumn, yPosition);
    yPosition += 5;
    // Gérer les adresses longues
    const addressLines = pdf.splitTextToSize(order.shippingAddress, 80);
    addressLines.forEach(line => {
      pdf.text(line, leftColumn, yPosition);
      yPosition += 5;
    });
    
    // Informations vendeur (colonne droite)
    let sellerY = 80;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('VENDEUR', rightColumn, sellerY);
    sellerY += 8;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('INZA STORE', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('contact@inza.com', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('+225 07 00 00 00 00', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('Abidjan, Cocody', rightColumn, sellerY);
    sellerY += 5;
    pdf.text('RC: CI-ABJ-2024-1234', rightColumn, sellerY);
    
    // Ligne de séparation
    yPosition += 15;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;
    
    // En-tête du tableau des produits
    pdf.setFillColor(240, 240, 240);
    pdf.rect(leftColumn, yPosition, pageWidth - 2 * margin, 10, 'F');
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PRODUIT', leftColumn + 5, yPosition + 7);
    pdf.text('CONFIGURATION', leftColumn + 70, yPosition + 7);
    pdf.text('QUANTITÉ', leftColumn + 120, yPosition + 7);
    pdf.text('PRIX U.', leftColumn + 145, yPosition + 7);
    pdf.text('TOTAL', leftColumn + 175, yPosition + 7);
    
    yPosition += 15;
    
    // Tableau des produits
    order.items.forEach((item, index) => {
      if (yPosition > pageHeight - 80) {
        pdf.addPage();
        yPosition = margin;
        
        // Répéter l'en-tête du tableau sur la nouvelle page
        pdf.setFillColor(240, 240, 240);
        pdf.rect(leftColumn, yPosition, pageWidth - 2 * margin, 10, 'F');
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.text('PRODUIT', leftColumn + 5, yPosition + 7);
        pdf.text('CONFIGURATION', leftColumn + 70, yPosition + 7);
        pdf.text('QUANTITÉ', leftColumn + 120, yPosition + 7);
        pdf.text('PRIX U.', leftColumn + 145, yPosition + 7);
        pdf.text('TOTAL', leftColumn + 175, yPosition + 7);
        yPosition += 15;
      }
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      
      // Gérer les noms de produits longs
      const nameLines = pdf.splitTextToSize(item.name, 60);
      nameLines.forEach((line, lineIndex) => {
        pdf.text(line, leftColumn + 5, yPosition + (lineIndex * 4));
      });
      
      // Gérer les configurations longues
      const configLines = pdf.splitTextToSize(item.config, 45);
      configLines.forEach((line, lineIndex) => {
        pdf.text(line, leftColumn + 70, yPosition + (lineIndex * 4));
      });
      
      // Alignement parfait des colonnes numériques
      pdf.text(item.quantity.toString(), leftColumn + 130, yPosition, { align: 'center' });
      pdf.text(formatPrice(item.price), leftColumn + 160, yPosition, { align: 'right' });
      pdf.text(formatPrice(item.price * item.quantity), leftColumn + 190, yPosition, { align: 'right' });
      
      yPosition += Math.max(nameLines.length * 4, configLines.length * 4, 8);
      
      // Ligne de séparation entre les produits
      if (index < order.items.length - 1) {
        pdf.setDrawColor(230, 230, 230);
        pdf.line(leftColumn, yPosition - 2, pageWidth - margin, yPosition - 2);
      }
    });
    
    yPosition += 10;
    
    // Ligne de séparation avant le total
    pdf.setDrawColor(150, 150, 150);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;
    
    // Sous-total
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Sous-total:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(order.total), leftColumn + 190, yPosition, { align: 'right' });
    yPosition += 6;
    
    // Livraison
    pdf.text('Livraison:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(0), leftColumn + 190, yPosition, { align: 'right' });
    yPosition += 8;
    
    // Total en gras avec ligne
    pdf.setDrawColor(100, 100, 100);
    pdf.line(leftColumn + 135, yPosition - 2, pageWidth - margin, yPosition - 2);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text('TOTAL:', leftColumn + 140, yPosition);
    pdf.text(formatPrice(order.total), leftColumn + 190, yPosition, { align: 'right' });
    
    // Méthode de paiement
    yPosition += 12;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Méthode de paiement: ${order.paymentMethod}`, leftColumn, yPosition);
    
    // Pied de page
    yPosition = pageHeight - 40;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(leftColumn, yPosition, pageWidth - margin, yPosition);
    
    yPosition += 10;
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Merci pour votre confiance!', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text('INZA STORE - Votre boutique de produits Apple en Côte d\'Ivoire', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text('contact@inza.com | +225 07 00 00 00 00', pageWidth / 2, yPosition, { align: 'center' });
    
    // Téléchargement du PDF
    pdf.save(`Facture-${order.id}.pdf`);
    
    // Message de confirmation
    console.log(`Facture PDF stylisée générée pour la commande ${order.id}`);
  };

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container>
          <EmptyState>
            <Package className="icon" />
            <h3>Connectez-vous pour voir vos commandes</h3>
            <p>Veuillez vous connecter pour accéder à l'historique de vos commandes.</p>
            <ShopButton onClick={() => navigate('/login')}>
              Se connecter
            </ShopButton>
          </EmptyState>
        </Container>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <LoadingState>
            <AlertCircle className="icon" />
            <h3>Chargement de vos commandes...</h3>
          </LoadingState>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <Header>
          <h1>Mes Commandes</h1>
          <p>Suivez l'état de vos commandes et consultez votre historique d'achats</p>
        </Header>

        <FiltersContainer>
          <SearchBar>
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher une commande..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchBar>
          
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="processing">En traitement</option>
            <option value="shipped">Expédiée</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
          </FilterSelect>
        </FiltersContainer>

        {filteredOrders.length === 0 ? (
          <EmptyState>
            <Package className="icon" />
            <h3>{searchQuery || statusFilter !== 'all' ? 'Aucune commande trouvée' : 'Aucune commande'}</h3>
            <p>
              {searchQuery || statusFilter !== 'all' 
                ? 'Essayez de modifier vos filtres de recherche.' 
                : 'Vous n\'avez pas encore passé de commande. Découvrez nos produits !'
              }
            </p>
            <ShopButton onClick={() => navigate('/boutique')}>
              {searchQuery || statusFilter !== 'all' ? 'Réinitialiser les filtres' : 'Commencer mes achats'}
            </ShopButton>
          </EmptyState>
        ) : (
          <OrdersGrid>
            {filteredOrders.map((order) => (
              <OrderCard key={order.id}>
                <OrderHeader>
                  <div className="order-number">{order.id}</div>
                  <div className={`order-status ${order.status}`}>
                    {getStatusIcon(order.status)}
                    {getStatusText(order.status)}
                  </div>
                </OrderHeader>
                
                <OrderDate>
                  <Calendar size={16} />
                  {formatDate(order.date)}
                </OrderDate>
                
                <OrderItems>
                  {order.items.map((item, index) => (
                    <div key={index} className="item">
                      <div className="item-icon">
                        <Smartphone size={24} />
                      </div>
                      <div className="item-details">
                        <div className="item-name">{item.name}</div>
                        <div className="item-config">{item.config}</div>
                      </div>
                      <div className="item-quantity">x{item.quantity}</div>
                    </div>
                  ))}
                </OrderItems>
                
                <OrderFooter>
                  <div className="order-total">{formatPrice(order.total)}</div>
                  <div className="order-actions">
                    <ActionButton onClick={() => handleViewDetails(order.id)}>
                      <Eye size={14} />
                      Détails
                    </ActionButton>
                    <ActionButton onClick={() => handleDownloadInvoice(order)}>
                      <Download size={14} />
                      Facture
                    </ActionButton>
                  </div>
                </OrderFooter>
              </OrderCard>
            ))}
          </OrdersGrid>
        )}
      </Container>
    </PageContainer>
  );
};

export default OrdersPageFixed;
